import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/common/PageHeader";
import { DetailSection, Metric, MetricStrip } from "@/components/common/AdminDetails";
import { EmptyState, ErrorState, LoadingRow } from "@/components/common/StateViews";
import { Card, CardContent } from "@/components/ui/card";
import { analyticsApi } from "@/lib/proxy-api";
import { ApiException } from "@/lib/api-client";
import { useHasInstance } from "@/lib/use-instance";
import { useTenantWorkspace } from "@/lib/tenant-workspace";

export function AnalyticsPage() {
  const hasInstance = useHasInstance();
  const workspace = useTenantWorkspace();
  const [days, setDays] = useState(30);
  const tenantId = workspace.tenantId ?? undefined;
  const enabled = hasInstance && workspace.hasTenant;

  const overviewQ = useQuery({
    queryKey: ["analytics-overview", tenantId],
    queryFn: () => analyticsApi.overview({ tenantId }),
    enabled,
  });
  const searchQ = useQuery({
    queryKey: ["analytics-search", tenantId, days],
    queryFn: () => analyticsApi.search({ tenantId, days }),
    enabled,
  });
  const docsQ = useQuery({
    queryKey: ["document-analytics", tenantId],
    queryFn: () => analyticsApi.documents({ tenantId }),
    enabled,
  });
  const usageQ = useQuery({
    queryKey: ["analytics-usage", tenantId, days],
    queryFn: () => analyticsApi.usage({ tenantId, days }),
    enabled,
  });
  const latencyQ = useQuery({
    queryKey: ["proxy-latency"],
    queryFn: () => analyticsApi.proxyLatency(),
    enabled: hasInstance,
    refetchInterval: 15_000,
  });

  const firstError = overviewQ.error ?? searchQ.error ?? docsQ.error ?? usageQ.error;

  if (!workspace.hasTenant) {
    return (
      <div className="space-y-6">
        <PageHeader title="Analytics" description="Tenant-scoped usage, search, and document health." />
        <EmptyState message="Select a tenant workspace to view analytics." />
      </div>
    );
  }

  if (firstError) {
    return (
      <div className="space-y-6">
        <PageHeader title="Analytics" description="Tenant-scoped usage, search, and document health." />
        <ErrorState message={firstError instanceof ApiException ? firstError.message : "Failed to load analytics"} />
      </div>
    );
  }

  const overview = overviewQ.data;
  const docs = docsQ.data;
  const search = searchQ.data;
  const usage = usageQ.data;

  return (
    <div className="space-y-6">
      <PageHeader title="Analytics" description="Tenant-scoped usage, search, document health, and Admin UI proxy latency." />

      <div className="flex items-center gap-2">
        <label htmlFor="analytics-days" className="text-sm font-medium">Window</label>
        <select
          id="analytics-days"
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          value={days}
          onChange={(event) => setDays(Number(event.target.value))}
        >
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
      </div>

      {overviewQ.isLoading || docsQ.isLoading || searchQ.isLoading || usageQ.isLoading ? (
        <LoadingRow cols={4} />
      ) : (
        <>
          <MetricStrip>
            <Metric label="Documents" value={docs?.total_documents ?? overview?.documents?.total ?? 0} note={`${docs?.active_documents ?? 0} active, ${docs?.archived_documents ?? 0} archived`} />
            <Metric label="Collections" value={overview?.collections?.total ?? "Unavailable"} note="Active collections" />
            <Metric label="Searches" value={usage?.total_search_calls ?? 0} note={`${usage?.avg_daily_searches ?? 0} per day avg`} />
            <Metric label="KB Status" value={overview?.knowledge_base?.status ?? "unknown"} note={overview?.knowledge_base?.id ?? "No knowledge base id"} />
          </MetricStrip>

          <div className="grid gap-4 lg:grid-cols-2">
            <AnalyticsTable
              title="Popular Queries"
              emptyLabel="No search logs in this window."
              headers={["Query", "Count"]}
              rows={(search?.popular_queries ?? []).map((query) => [query.query, query.count])}
            />
            <AnalyticsTable
              title="Daily Search Calls"
              emptyLabel="No daily usage data in this window."
              headers={["Date", "Calls"]}
              rows={Object.entries(usage?.daily_search_calls ?? {}).sort(([left], [right]) => right.localeCompare(left))}
            />
            <AnalyticsTable
              title="Documents By Status"
              emptyLabel="No document status data."
              headers={["Status", "Documents"]}
              rows={Object.entries(docs?.by_status ?? {}).sort((a, b) => b[1] - a[1])}
            />
            <AnalyticsTable
              title="Documents By File Type"
              emptyLabel="No file type data."
              headers={["Type", "Documents"]}
              rows={Object.entries(docs?.by_file_type ?? {}).sort((a, b) => b[1] - a[1])}
            />
          </div>

          <DetailSection title="Performance">
            <MetricStrip>
              <Metric label="Avg Search Time" value={`${search?.performance_metrics?.avg_execution_time_ms ?? 0} ms`} note="ServisSearch search logs" />
              <Metric label="Avg Results" value={search?.performance_metrics?.avg_results_count ?? 0} note="Per search" />
              <Metric label="Proxy p50" value={`${latencyQ.data?.p50 ?? 0} ms`} note={`${latencyQ.data?.count ?? 0} Admin UI calls sampled`} />
              <Metric label="Proxy p95" value={`${latencyQ.data?.p95 ?? 0} ms`} note="Admin UI upstream proxy" />
            </MetricStrip>
          </DetailSection>

          <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
            Current ServisSearch analytics are useful but limited: feedback submissions are a placeholder, search logs are capped at 1000 records per query, and document analytics are not paginated or broken down per collection by status.
          </div>
        </>
      )}
    </div>
  );
}

function AnalyticsTable({
  title,
  headers,
  rows,
  emptyLabel,
}: {
  title: string;
  headers: [string, string];
  rows: Array<[string, string | number]>;
  emptyLabel: string;
}) {
  return (
    <Card>
      <CardContent className="space-y-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{headers[0]}</th>
                  <th className="w-28 px-3 py-2 text-right font-medium">{headers[1]}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, value]) => (
                  <tr key={label} className="border-t">
                    <td className="px-3 py-2">{label}</td>
                    <td className="px-3 py-2 text-right font-medium">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
